import { motion } from "framer-motion";
import { BookOpen } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogTrigger,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";

// Renders **bold** segments in gold within a plain string.
function RichText({ text }) {
    const parts = text.split("**");
    return (
        <>
            {parts.map((part, i) =>
                i % 2 === 1 ? (
                    <strong key={i} className="font-medium text-gold">
                        {part}
                    </strong>
                ) : (
                    <span key={i}>{part}</span>
                ),
            )}
        </>
    );
}

export default function BioDialog({ member }) {
    return (
        <Dialog>
            <DialogTrigger asChild>
                <button
                    data-testid={`read-bio-${member.name
                        .toLowerCase()
                        .replace(/\s+/g, "-")}`}
                    className="group mt-5 inline-flex items-center gap-2.5 border border-gold/50 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-gold transition-all duration-300 hover:bg-gold hover:text-navy-950"
                >
                    <BookOpen className="h-3.5 w-3.5" />
                    Read Bio
                </button>
            </DialogTrigger>

            <DialogContent
                data-testid="bio-dialog"
                className="flex max-h-[88vh] w-[calc(100%-2rem)] max-w-2xl flex-col overflow-hidden border-white/10 bg-navy-900 p-0 text-white sm:rounded-none"
            >
                {/* Header */}
                <div className="relative shrink-0 overflow-hidden border-b border-white/10 bg-navy-950 px-8 pb-8 pt-10 md:px-10">
                    <span className="pointer-events-none absolute -right-4 -top-10 select-none font-display text-[10rem] leading-none text-white/[0.03]">
                        H
                    </span>
                    <div className="relative flex items-center gap-5">
                        <div className="h-20 w-20 shrink-0 overflow-hidden border border-gold/30">
                            <img
                                src={member.img}
                                alt={member.name}
                                className="h-full w-full object-cover object-top grayscale"
                            />
                        </div>
                        <div>
                            <DialogTitle className="font-display text-2xl md:text-3xl font-light text-white">
                                {member.name}
                            </DialogTitle>
                            <DialogDescription className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-gold">
                                {member.role}
                            </DialogDescription>
                        </div>
                    </div>
                    {member.bioLead && (
                        <p className="relative mt-6 font-display text-lg font-light italic leading-snug text-white/80">
                            {member.bioLead}
                        </p>
                    )}
                    <div className="hairline-gold absolute bottom-0 left-0 h-px w-full" />
                </div>

                {/* Body */}
                <motion.div
                    data-lenis-prevent
                    initial="hidden"
                    animate="show"
                    variants={{
                        hidden: {},
                        show: { transition: { staggerChildren: 0.06, delayChildren: 0.1 } },
                    }}
                    className="min-h-0 flex-1 space-y-5 overflow-y-auto px-8 py-8 md:px-10"
                >
                    {member.bio.map((para, i) => (
                        <motion.p
                            key={i}
                            variants={{
                                hidden: { opacity: 0, y: 14 },
                                show: {
                                    opacity: 1,
                                    y: 0,
                                    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
                                },
                            }}
                            className="text-sm md:text-[15px] leading-relaxed text-white/70"
                        >
                            <RichText text={para} />
                        </motion.p>
                    ))}
                    <motion.div
                        variants={{
                            hidden: { opacity: 0 },
                            show: { opacity: 1, transition: { duration: 0.8 } },
                        }}
                        className="flex items-center gap-3 pt-2"
                    >
                        <span className="h-px w-10 bg-gold/50" />
                        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
                            HEIR PTE. LTD.
                        </span>
                    </motion.div>
                </motion.div>
            </DialogContent>
        </Dialog>
    );
}
